import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CertificateGenerator } from "./CertificateGenerator";
import { Download, Loader2, Linkedin } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import html2canvas from "html2canvas";

interface CertificateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  blogPostTitle: string;
  roadmapId: string;
  userId: string;
}

export const CertificateModal = ({
  open,
  onOpenChange,
  blogPostTitle,
  roadmapId,
  userId,
}: CertificateModalProps) => {
  const [learnerName, setLearnerName] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [certificateNumber, setCertificateNumber] = useState("");
  const [issuedDate, setIssuedDate] = useState("");
  const [existingCertificate, setExistingCertificate] = useState(false);
  const { toast } = useToast();

  // Check for existing certificate when modal opens
  useEffect(() => {
    const checkExistingCertificate = async () => {
      try {
        const { data, error } = await supabase
          .from("certificates")
          .select("learner_name, certificate_number, issued_at")
          .eq("user_id", userId)
          .eq("roadmap_id", roadmapId)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setLearnerName(data.learner_name);
          setCertificateNumber(data.certificate_number);
          setIssuedDate(new Date(data.issued_at).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          }));
          setExistingCertificate(true);
        } else {
          // Generate certificate number for new certificate
          const timestamp = Date.now();
          const random = Math.random().toString(36).substring(2, 6).toUpperCase();
          setCertificateNumber(`RB-${timestamp}-${random}`);
          setIssuedDate(new Date().toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          }));
          setExistingCertificate(false);
        }
      } catch (error) {
        console.error("Error checking certificate:", error);
      }
    };

    if (open) {
      checkExistingCertificate();
    }
  }, [open, userId, roadmapId]);

  const saveCertificate = async () => {
    if (!learnerName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter your name for the certificate.",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase.from("certificates").insert({
        user_id: userId,
        roadmap_id: roadmapId,
        blog_post_title: blogPostTitle,
        learner_name: learnerName.trim(),
        certificate_number: certificateNumber,
      });

      if (error) throw error;

      setExistingCertificate(true);
      toast({
        title: "Certificate Saved",
        description: "Your certificate has been saved successfully.",
      });
    } catch (error) {
      console.error("Error saving certificate:", error);
      toast({
        title: "Error",
        description: "Failed to save certificate. Please try again.",
        variant: "destructive",
      });
    }
  };

  const shareOnLinkedIn = async () => {
    if (!learnerName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter your name before sharing.",
        variant: "destructive",
      });
      return;
    }

    // Save certificate to database if not already saved
    if (!existingCertificate) {
      await saveCertificate();
    }

    const verificationUrl = `https://www.runoblog.site/verify/${certificateNumber}`;
    const text = `I'm excited to share that I've completed the 28-Day Learning Roadmap: "${blogPostTitle}" on RunoBlog! 🎓\n\nVerify my certificate: ${verificationUrl}`;
    
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verificationUrl)}&summary=${encodeURIComponent(text)}`;
    
    window.open(linkedInUrl, "_blank", "width=600,height=600");
    
    toast({
      title: "Opening LinkedIn",
      description: "Share your achievement with your network!",
    });
  };

  const downloadCertificate = async () => {
    if (!learnerName.trim()) {
      toast({
        title: "Name Required",
        description: "Please enter your name before downloading.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsGenerating(true);

      // Save certificate to database if not already saved
      if (!existingCertificate) {
        await saveCertificate();
      }

      // Generate certificate image
      const certificateElement = document.getElementById("certificate-content");
      if (!certificateElement) throw new Error("Certificate element not found");

      const canvas = await html2canvas(certificateElement, {
        scale: 3,
        backgroundColor: null,
        logging: false,
        useCORS: true,
      });

      // Convert to blob and download
      canvas.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `RunoBlog_Certificate_${learnerName.replace(/\s+/g, "_")}_${certificateNumber}.png`;
        link.click();
        URL.revokeObjectURL(url);

        toast({
          title: "Certificate Downloaded",
          description: "Your certificate has been downloaded successfully!",
        });
      });
    } catch (error) {
      console.error("Error generating certificate:", error);
      toast({
        title: "Error",
        description: "Failed to generate certificate. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[900px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Your Certificate of Completion</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Name Input */}
          <div className="space-y-2">
            <Label htmlFor="learner-name">Your Name (as it will appear on certificate)</Label>
            <Input
              id="learner-name"
              value={learnerName}
              onChange={(e) => setLearnerName(e.target.value)}
              placeholder="Enter your full name"
              disabled={existingCertificate}
            />
            {existingCertificate && (
              <p className="text-xs text-muted-foreground">
                Certificate already issued. Name cannot be changed.
              </p>
            )}
          </div>

          {/* Certificate Preview */}
          <div className="flex justify-center overflow-x-auto">
            <div className="scale-90 origin-top">
              <CertificateGenerator
                learnerName={learnerName || "Your Name Here"}
                blogPostTitle={blogPostTitle}
                completionDate={issuedDate}
                certificateNumber={certificateNumber}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Button
              onClick={shareOnLinkedIn}
              disabled={isGenerating || !learnerName.trim()}
              variant="outline"
              size="lg"
              className="w-full"
            >
              <Linkedin className="mr-2 h-5 w-5" />
              Share on LinkedIn
            </Button>
            
            <Button
              onClick={downloadCertificate}
              disabled={isGenerating || !learnerName.trim()}
              className="w-full"
              size="lg"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-5 w-5" />
                  Download
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
